import { CreateGameOptions, TriviaList } from '@repo/types'
import { Container, Description, Heading, HistoryIcon, Spinner } from '@repo/ui'
import * as Sentry from '@sentry/react'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'

import { AppHeader } from '@/components/AppHeader'
import { AppHeaderLogo } from '@/components/AppHeaderLogo'
import { Button } from '@/components/Button'
import { BackButton, SettingsButton } from '@/components/icon-buttons/IconButtons'
import { IconButton } from '@/components/IconButton'
import { Main } from '@/components/Main'
import { NavigationLink } from '@/components/NavigationLink'
import { Page } from '@/components/Page'
import { SubscriptionActionRequired } from '@/components/SubscriptionActionRequired'
import { TermsOfService } from '@/components/TermsOfService'
import { MixpanelService } from '@/mixpanel'
import StoreService from '@/store/StoreService'

import { useQuerySystemTriviaLists } from '../queries/useQuerySystemTriviaLists'
import { useQueryUserTriviaLists } from '../queries/useQueryUserTriviaLists'
import { SystemTriviaLists } from './components/SystemTriviaLists'
import { TriviaListPreviewModal } from './components/TriviaListPreviewModal'
import { UserTriviaLists } from './UserTriviaLists'

export const ChooseTriviaList = observer(() => {
  const { createGame, triviaListService, user } = StoreService.getStore()

  const { isLoading: isLoadingSystemTriviaLists } = useQuerySystemTriviaLists()
  useQueryUserTriviaLists()

  const [isCreatingGame, setIsCreatingGame] = useState(false)
  const [showCreateGameError, setShowCreateGameError] = useState(false)

  const [showTriviaListPreviewModal, setShowTriviaListPreviewModal] = useState(false)

  const [loadingTriviaList, setLoadingTriviaList] = useState(false)
  const [selectedTriviaList, setSelectedTriviaList] = useState<TriviaList | null>(null)

  const handleSystemTriviaListClick = async (listId: string) => {
    setShowTriviaListPreviewModal(true)
    setLoadingTriviaList(true)

    const triviaList = await triviaListService.querySystemTriviaList(listId)

    if (triviaList) {
      setSelectedTriviaList(triviaList)
      setLoadingTriviaList(false)
    }
  }

  const handleUserTriviaListClick = async (listId: string) => {
    setLoadingTriviaList(true)
    setShowTriviaListPreviewModal(true)

    const triviaList = await triviaListService.queryUserTriviaList(listId)

    if (triviaList) {
      setSelectedTriviaList(triviaList)
      setLoadingTriviaList(false)
    }
  }

  const handleCreateGameClick = async () => {
    if (!selectedTriviaList) return
    setIsCreatingGame(true)

    try {
      const options: CreateGameOptions = {
        triviaList: selectedTriviaList,
        type: 'trivia'
      }

      const { gameId } = await createGame(options)
      MixpanelService.createGameTrivia(gameId, options)
    } catch (error) {
      setShowCreateGameError(true)
      setIsCreatingGame(false)
      Sentry.captureException(error)
    }

    setShowTriviaListPreviewModal(false)
  }

  // if (showCreateGameError) {
  //   return <CreateGameError />
  // }

  if (isCreatingGame) {
    return <Spinner label='Creating game...' />
  }

  return (
    <Page title='Choose a Trivia List'>
      <AppHeader
        actionsLeft={<BackButton url='/' />}
        actionsRight={
          <>
            <IconButton icon={<HistoryIcon />} title='Game History' url='/game-history' />

            <SettingsButton url='/settings' />
          </>
        }
        header={
          <NavigationLink className='flex h-full items-center' to='/'>
            <AppHeaderLogo />
          </NavigationLink>
        }
      />

      <Main>
        {user.isSubscriptionActionRequired && (
          <Container className='mb-12 flex flex-col gap-12' size='extra-large'>
            <SubscriptionActionRequired />
          </Container>
        )}

        <Container className='mb-6 flex flex-col gap-4 lg:mb-12' size='extra-large'>
          <div className='flex flex-col gap-2'>
            <Heading size='large'>Trivia Bingo</Heading>
            <Description>Choose a trivia list below to start a game of trivia bingo.</Description>
            <Button text='Change Game Type' to='/' variant='anchor' />
          </div>
        </Container>

        <Container className='flex flex-col gap-12' size='extra-large'>
          <SystemTriviaLists onClick={handleSystemTriviaListClick} />

          {!isLoadingSystemTriviaLists && <UserTriviaLists onClick={handleUserTriviaListClick} />}
        </Container>

        <TermsOfService />
      </Main>

      {/* {selectedPlaylist && (
        <PlaylistErrorModal
          isVisible={showPlaylistErrorModal}
          playlist={selectedPlaylist}
          onClose={() => {
            setShowPlaylistErrorModal(false)
            setSelectedPlaylist(null)
          }}
        />
      )} */}

      <TriviaListPreviewModal
        isLoading={loadingTriviaList}
        isVisible={showTriviaListPreviewModal}
        triviaList={selectedTriviaList}
        onClickCreateGame={handleCreateGameClick}
        onClose={() => {
          setShowTriviaListPreviewModal(false)
          setSelectedTriviaList(null)
        }}
      />
    </Page>
  )
})
